import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isHqHost, setHqBranch } from '../services/api';
import { isMobileApp } from '../utils/platform';

// 2026-10-18 — Red Sea Transport rebrand (dark theme, two-pane layout,
// amber accent, Space Grotesk display face). The business logic is
// unchanged from the Red Sea Distribution Login — only the visual
// identity differs. Compact (Electron APK) branch is preserved for the
// off-chance Transport goes mobile later; it also uses the new palette
// via inline styles so there's no shared stylesheet dependency.
const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isNarrow, setIsNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 820);
  const { login } = useAuth();
  const navigate = useNavigate();
  const hq = isHqHost();

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 820);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/settings/business/public')
      .then(r => { if (!cancelled) setBusinessName(r.data?.business_name || ''); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (hq) setHqBranch('');
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const compact = isMobileApp();
  if (compact) {
    return (
      <div style={S.compactPage}>
        <div style={S.compactBrand}>
          <div style={S.logoMark}>RT</div>
          <h1 style={S.compactTitle}>RED SEA TRANSPORT</h1>
          <p style={S.compactSub}>Operations Platform</p>
        </div>
        {error && <div style={S.err}>{error}</div>}
        <form style={S.compactForm} onSubmit={handleSubmit}>
          <label style={S.lbl}>USERNAME
            <input style={S.input} type="text" value={email} onChange={e => setEmail(e.target.value)}
              autoCapitalize="none" autoCorrect="off" autoComplete="username" required />
          </label>
          <label style={S.lbl}>PASSWORD
            <div style={{position:'relative'}}>
              <input style={S.input} type={showPassword ? 'text' : 'password'} value={password}
                onChange={e => setPassword(e.target.value)} autoComplete="current-password" required />
              <button type="button" onClick={() => setShowPassword(v => !v)} style={S.showBtn}>
                {showPassword ? 'HIDE' : 'SHOW'}
              </button>
            </div>
          </label>
          <button type="submit" style={{...S.primary, opacity: loading ? 0.6 : 1}} disabled={loading}>
            {loading ? 'SIGNING IN…' : 'CONTINUE'}
          </button>
        </form>
        <div style={S.compactFoot}>Red Sea Import &amp; Export (Z) Ltd</div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={{...S.stage, gridTemplateColumns: isNarrow ? '1fr' : '1.1fr 1fr'}}>

        {/* LEFT PANE — brand + marketing */}
        <div style={{...S.left, borderRight: isNarrow ? 'none' : `1px solid ${T.border}`, borderBottom: isNarrow ? `1px solid ${T.border}` : 'none', padding: isNarrow ? '28px 32px' : '44px 48px'}}>
          <div style={S.brandMark}>
            <div style={S.logoMark}>RT</div>
            <div>
              <div style={S.brandName}>RED SEA TRANSPORT</div>
              <div style={S.brandTag}>OPERATIONS PLATFORM</div>
            </div>
          </div>

          <div>
            <div style={S.marketing}>Move <span style={{color: T.accent}}>freight</span>,<br/>not paperwork.</div>
            <div style={S.marketingSub}>
              Issue tax invoices in seconds. Track every claim, offload and levy against the customer.
              Fiscalise directly with ZRA Smart Invoice.
            </div>
          </div>

          <div style={S.metaStrip}>
            <span>ZRA SMART INVOICE · <b style={{color: T.ink}}>ACTIVE</b></span>
            <span>LUSAKA · <b style={{color: T.ink}}>ZM</b></span>
          </div>
        </div>

        {/* RIGHT PANE — form */}
        <div style={{...S.right, padding: isNarrow ? '32px 32px 40px' : '56px 60px'}}>
          <div style={S.formTitle}>Sign in</div>
          <div style={S.formSub}>{businessName || 'Use your operations account to continue.'}</div>

          {error && <div style={S.err}>{error}</div>}

          <form onSubmit={handleSubmit}>
            <div style={S.field}>
              <label style={S.lbl}>USERNAME</label>
              <input style={S.input} type="text" value={email} onChange={e => setEmail(e.target.value)}
                autoCapitalize="none" autoCorrect="off" autoComplete="username" required />
            </div>
            <div style={S.field}>
              <label style={S.lbl}>PASSWORD</label>
              <div style={{position:'relative'}}>
                <input style={S.input} type={showPassword ? 'text' : 'password'} value={password}
                  onChange={e => setPassword(e.target.value)} autoComplete="current-password" required />
                <button type="button" onClick={() => setShowPassword(v => !v)} style={S.showBtn}>
                  {showPassword ? 'HIDE' : 'SHOW'}
                </button>
              </div>
            </div>

            <button type="submit" style={{...S.primary, opacity: loading ? 0.6 : 1}} disabled={loading}>
              {loading ? 'SIGNING IN…' : 'CONTINUE'}
            </button>
          </form>

          <div style={S.formFoot}>
            <span>Secure terminal</span>
            <span>v2.0</span>
          </div>
        </div>

      </div>
    </div>
  );
};

// ------- design tokens -------
const T = {
  bg:           '#0b0f17',
  surface1:     '#141924',
  surface2:     '#1c2230',
  surface3:     '#263040',
  border:       '#2a3344',
  borderStrong: '#3b465c',
  ink:          '#e8e6e0',
  inkDim:       '#94a1b8',
  inkMuted:     '#5f6d85',
  accent:       '#f59e0b',
  accentGlow:   '#fbbf24',
  accentInk:    '#0b0f17',
  warn:         '#f87171',
  // Inter for everything — the Space Grotesk display face read as
  // "AI-generated" against the rest of the app chrome. Keep Inter
  // consistent across Login + POS + the pages still to be redone.
  display:      "'Inter', -apple-system, 'Segoe UI', sans-serif",
  sans:         "'Inter', -apple-system, 'Segoe UI', sans-serif",
  // Keep every face Inter; numeric alignment handled with
  // font-variant-numeric: tabular-nums where columns line up.
  mono:         "'Inter', -apple-system, 'Segoe UI', sans-serif",
};

// ------- styles -------
const S = {
  page: {
    position: 'fixed', inset: 0,
    background: `radial-gradient(80% 60% at 85% 15%, rgba(245,158,11,0.08), transparent 60%),
                  radial-gradient(60% 50% at 10% 85%, rgba(245,158,11,0.05), transparent 60%),
                  ${T.bg}`,
    display: 'grid', placeItems: 'center',
    fontFamily: T.sans, color: T.ink,
    overflow: 'auto', padding: 20,
  },
  stage: {
    width: '100%', maxWidth: 1000, minHeight: 540,
    display: 'grid', gridTemplateColumns: '1.1fr 1fr',
    background: T.surface1, border: `1px solid ${T.border}`,
    borderRadius: 10, overflow: 'hidden',
    boxShadow: '0 25px 60px rgba(0,0,0,0.45)',
  },
  left: {
    padding: '44px 48px',
    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
    borderRight: `1px solid ${T.border}`,
    background: T.bg,
  },
  brandMark: {
    display: 'flex', alignItems: 'center', gap: 14,
  },
  logoMark: {
    width: 48, height: 48,
    background: T.accent, color: T.accentInk,
    borderRadius: 11,
    display: 'grid', placeItems: 'center',
    fontFamily: T.display, fontWeight: 700, fontSize: 22,
    boxShadow: '0 0 0 4px rgba(245,158,11,0.1)',
  },
  brandName: {
    fontFamily: T.display, fontWeight: 700,
    fontSize: 17, letterSpacing: 0.3, color: T.ink,
  },
  brandTag: {
    fontWeight: 500, fontSize: 10.5,
    color: T.inkDim, letterSpacing: 2, textTransform: 'uppercase', marginTop: 2,
  },
  marketing: {
    fontFamily: T.display,
    fontSize: 34, lineHeight: 1.1, fontWeight: 600,
    marginTop: 56, textWrap: 'balance',
  },
  marketingSub: {
    color: T.inkDim, fontSize: 13.5, marginTop: 16,
    maxWidth: '38ch', lineHeight: 1.55,
  },
  metaStrip: {
    display: 'flex', gap: 24, fontSize: 11,
    color: T.inkMuted, fontFamily: T.mono,
    marginTop: 24,
  },
  right: {
    padding: '56px 60px',
    display: 'flex', flexDirection: 'column', justifyContent: 'center',
    background: `linear-gradient(180deg, ${T.surface1}, ${T.bg} 70%)`,
  },
  formTitle: {
    fontFamily: T.display,
    fontSize: 22, fontWeight: 600, marginBottom: 6, color: T.ink,
  },
  formSub: {
    color: T.inkDim, fontSize: 13, marginBottom: 28,
  },
  field: { marginBottom: 16 },
  lbl: {
    display: 'block',
    fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1,
    color: T.inkDim, fontWeight: 600, marginBottom: 6,
  },
  input: {
    width: '100%',
    background: T.surface2, color: T.ink,
    border: `1px solid ${T.border}`, borderRadius: 6,
    padding: '11px 14px', fontSize: 14, fontFamily: T.sans,
    outline: 'none', boxSizing: 'border-box',
  },
  showBtn: {
    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
    background: 'transparent', color: T.inkDim, border: 'none',
    fontSize: 10, fontWeight: 700, letterSpacing: 1, cursor: 'pointer',
    padding: '4px 8px',
  },
  primary: {
    width: '100%',
    background: T.accent, color: T.accentInk,
    border: 'none', borderRadius: 6,
    padding: '13px 16px', fontSize: 14, fontWeight: 700, letterSpacing: 0.5,
    fontFamily: T.display, cursor: 'pointer',
    textTransform: 'uppercase', marginTop: 8,
  },
  err: {
    background: 'rgba(248,113,113,0.1)', color: T.warn,
    border: `1px solid ${T.warn}`, borderRadius: 6,
    padding: '10px 14px', fontSize: 13, marginBottom: 16,
  },
  formFoot: {
    marginTop: 24, fontSize: 11.5, color: T.inkMuted,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    fontFamily: T.mono,
  },

  // compact (APK) variant
  compactPage: {
    position: 'fixed', inset: 0, background: T.bg, color: T.ink,
    fontFamily: T.sans, padding: 24,
    display: 'flex', flexDirection: 'column',
  },
  compactBrand: {
    textAlign: 'center', marginTop: 48, marginBottom: 32,
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
  },
  compactTitle: {
    fontFamily: T.display, fontSize: 20, fontWeight: 700,
    letterSpacing: 1, margin: 0, color: T.ink,
  },
  compactSub: {
    fontSize: 11, color: T.inkDim, letterSpacing: 2,
    textTransform: 'uppercase', margin: 0,
  },
  compactForm: {
    display: 'flex', flexDirection: 'column', gap: 14,
    maxWidth: 400, width: '100%', margin: '0 auto',
  },
  compactFoot: {
    textAlign: 'center', fontSize: 11, color: T.inkMuted,
    marginTop: 'auto', paddingTop: 24, fontFamily: T.mono,
  },
};

export default Login;
